'use client';

import React from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { AlertTriangle } from 'lucide-react';

interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info';
    isLoading?: boolean;
}

export function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    isLoading = false,
}: ConfirmModalProps) {
    const handleConfirm = () => {
        onConfirm();
    };

    const variantStyles = {
        danger: {
            icon: 'text-red-600 bg-red-100',
            button: 'secondary' as const,
        },
        warning: {
            icon: 'text-amber-600 bg-amber-100',
            button: 'primary' as const,
        },
        info: {
            icon: 'text-hsecondary bg-blue-100',
            button: 'primary' as const,
        },
    };

    const style = variantStyles[variant];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm">
            <div className="flex flex-col items-center text-center">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-full ${style.icon} flex items-center justify-center mb-4`}>
                    <AlertTriangle className="w-6 h-6" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-hsecondary mb-2">{title}</h3>

                {/* Message */}
                <p className="text-hmuted mb-6">{message}</p>

                {/* Actions */}
                <div className="flex gap-3 w-full justify-center">
                    <button
                        className='px-6 py-2 border border-slate-200 rounded-full text-slate-700 hover:bg-slate-100 transition-colors'
                        onClick={onClose}
                    >
                        {cancelText}
                    </button>
                    <button
                        className='px-6 py-2 border border-red-600 rounded-full text-red-600 hover:bg-red-100 transition-colors bg-red-50'
                        onClick={handleConfirm}
                    >
                        {confirmText}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
